import { type Lang } from "@/lib/i18n";
import { type SubPageContent } from "@/components/common/SubPageLayout";

const FIVE_LAYER_IMAGE =
  "https://mutualfilter.com/wp-content/uploads/2026/09/Sintered-Mesh-Filter-Elements-of-alloyfilter.jpg?v=hy0enjdkewc";
const THREE_LAYER_IMAGE =
  "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/sintered_five_layer_mesh_structure.png";
const MULTI_LAYER_IMAGE =
  "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-precision-s-cb8314.png";
const MULTILAYER_HERO_IMAGE =
  "https://mutualfilter.com/wp-content/uploads/2026/09/Multi-Layer-Sintered-Woven-Wire-Mesh-Discs.jpg";

export const COPY: Record<Lang, SubPageContent> = {
  en: {
    breadcrumbKey: "LayerStandardSinteredMesh",
    relatedTopic: "sintered",
    hero: {
      categoryBadge: "Sintered Multilayer Mesh",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "5 Layer Standard Sintered Mesh",
      lead: "The universal industrial configuration combining a protective layer, control layer, dispersion layer, and two reinforcing skeleton layers for unsurpassed structural rigidity.",
      secondaryCta: {
        label: "Multilayer Overview",
        to: "/SinteredMultilayerMeshPage",
      },
      image: {
        src: FIVE_LAYER_IMAGE,
        alt: "5-layer standard sintered wire mesh laminate structure",
      },
      cardNote: "Universal 5-Layer Benchmark",
      cardLink: {
        label: "Request Specifications",
        to: "/RequestAQuotePage",
      },
      specs: [
        { label: "Filtration Rating", value: "1 – 200 µm" },
        { label: "Standard Alloys", value: "AISI 316L / 304L" },
        { label: "Nominal Thickness", value: "1.70 mm standard" },
        { label: "Construction", value: "5-layer diffusion bonded" },
      ],
    },
    overview: {
      eyebrow: "Industry Benchmark",
      heading: "Universal Five-Layer Diffusion-Bonded Geometry",
      paragraphs: [
        "The 5-layer standard sintered wire mesh is the universally adopted benchmark in industrial porous metal filtration. It strategically aligns five layers of woven wire cloth, diffusion-bonding them in a high-vacuum furnace into an integrated porous plate.",
        "The stacking architecture proceeds in an engineered sequence: Layer 1 is a protective mesh guarding against surface mechanical wear; Layer 2 is the precise micro-pore control layer; Layer 3 acts as a flow dispersion buffer; while Layers 4 and 5 form a heavy double-skeleton support grid providing exceptional mechanical stability.",
        "Because wire intersections across all five tiers are metallurgically locked by solid-state diffusion, the pore structure resists shifting or deformation during continuous pressurized filtration, reverse pulse blowbacks, and chemical CIP wash cycles.",
      ],
      keyPoints: [
        "Universal 5-tier architecture: protective, control, buffer, and two heavy support layers.",
        "Solid-state diffusion bonds all wire intersections for permanent micron aperture precision.",
        "Withstands cyclic reverse backflushing and high differential pressure without media migration.",
      ],
      image: {
        src: FIVE_LAYER_IMAGE,
        alt: "5-layer standard sintered wire mesh laminate structure",
      },
    },
    specTable: {
      heading: "Standard 5-Layer Specification Matrix",
      intro: "Standard sheets produced in AISI 316L and 304L stainless steels at a nominal thickness of 1.70 mm.",
      columns: [
        { key: "specification", label: "Model Spec", align: "left" },
        { key: "nominalRating", label: "Rating (µm)", align: "right" },
        { key: "controlMesh", label: "Control Layer", align: "right" },
        { key: "nominalThickness", label: "Thickness (mm)", align: "right" },
        { key: "standardDimensions", label: "Standard Sheet Size", align: "left" },
      ],
      rows: [
        {
          specification: "5L-001",
          nominalRating: "1",
          controlMesh: "Dutch Weave",
          nominalThickness: "1.70",
          standardDimensions: "500 × 1000 mm / 1000 × 1000 mm",
        },
        {
          specification: "5L-005",
          nominalRating: "5",
          controlMesh: "Dutch Weave",
          nominalThickness: "1.70",
          standardDimensions: "500 × 1000 mm / 1000 × 1000 mm",
        },
        {
          specification: "5L-010",
          nominalRating: "10",
          controlMesh: "Dutch Weave",
          nominalThickness: "1.70",
          standardDimensions: "500 × 1000 mm / 1000 × 1000 mm",
        },
        {
          specification: "5L-025",
          nominalRating: "25",
          controlMesh: "500 Mesh",
          nominalThickness: "1.70",
          standardDimensions: "500 × 1000 mm / 1000 × 1000 mm",
        },
        {
          specification: "5L-050",
          nominalRating: "50",
          controlMesh: "300 Mesh",
          nominalThickness: "1.70",
          standardDimensions: "500 × 1000 mm / 1000 × 1000 mm",
        },
        {
          specification: "5L-100",
          nominalRating: "100",
          controlMesh: "150 Mesh",
          nominalThickness: "1.70",
          standardDimensions: "500 × 1000 mm / 1000 × 1000 mm",
        },
        {
          specification: "5L-200",
          nominalRating: "200",
          controlMesh: "80 Mesh",
          nominalThickness: "1.70",
          standardDimensions: "500 × 1000 mm / 1000 × 1000 mm",
        },
      ],
      caption: "Custom plate sizes up to 1200 × 1500 mm available upon engineering consultation.",
    },
    features: {
      heading: "Benchmark Engineering Characteristics",
      intro: "Proven across chemical, pharmaceutical, petrochemical, and polymer processing installations.",
      items: [
        {
          title: "Fixed Aperture Stability",
          text: "Thermal diffusion fuses wire intersections, guaranteeing that micron aperture size remains unchanged under continuous operational pressure.",
        },
        {
          title: "Exceptional Mechanical Strength",
          text: "Dual reinforcing skeleton layers endow the composite with immense structural rigidity, resisting bending and burst forces.",
        },
        {
          title: "Superior Cleanability",
          text: "Smooth surface pore distribution permits rapid particle detachment during forward flush, reverse backwash, and ultrasonic cleaning.",
        },
        {
          title: "Comprehensive Machinability",
          text: "Can be laser cut, CNC punched, rolled into seamless cylinders, pleated into cartridges, and TIG welded to custom flanges.",
        },
      ],
    },
    related: {
      heading: "Related Sintered Mesh Constructions",
      intro: "Compare standard 5-layer plates with 3-layer high-flow and reinforced multi-layer composites.",
      cards: [
        {
          title: "Sintered Multilayer Mesh",
          text: "The parent family encompassing standard 3-layer, 5-layer, and multi-tier diffusion-bonded composite laminates.",
          to: "/SinteredMultilayerMeshPage",
          image: {
            src: MULTILAYER_HERO_IMAGE,
            alt: "Vacuum diffusion bonded sintered multilayer wire mesh laminate",
          },
          badge: "Parent Category",
          linkLabel: "View Multilayer Overview",
        },
        {
          title: "3 Layer Sintered Mesh",
          text: "Engineered with protective outer mesh, precision control layer, and open support grid for high flow permeability.",
          to: "/ThreeLayerSinteredMeshPage",
          image: {
            src: THREE_LAYER_IMAGE,
            alt: "Cross section of 3-layer sintered stainless steel mesh laminate",
          },
          badge: "3-Layer",
          linkLabel: "View 3-Layer Specifications",
        },
        {
          title: "Multi Layer Sintered Mesh",
          text: "Vacuum-sintered multi-layer wire mesh combined with rigid perforated plates or heavy reinforcement meshes.",
          to: "/MultiLayerPage",
          image: {
            src: MULTI_LAYER_IMAGE,
            alt: "Macro view of multi-layer sintered stainless steel wire mesh laminate",
          },
          badge: "Heavy Reinforcement",
          linkLabel: "View Multi-Layer Specifications",
        },
      ],
    },
    cta: {
      id: "layer-standard-sintered-cta",
      eyebrow: "Universal Stock & Custom Cutting",
      heading: "Order Standard 5-Layer Sintered Wire Mesh",
      lead: "We maintain stock of standard 1.70 mm 5-layer plates in AISI 316L and 304L, providing fast dispatch of full sheets, precision-cut discs, and fully welded filter cartridges.",
      secondaryLabel: "Contact Sales Team",
      secondaryTo: "/ContactPage",
      note: "Standard 1.70 mm sheets and fabricated components supplied with certified quality inspection reports upon request.",
    },
  },
  zh: {
    breadcrumbKey: "LayerStandardSinteredMesh",
    relatedTopic: "sintered",
    hero: {
      categoryBadge: "多层烧结复合网",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "5 层标准烧结网",
      lead: "通用工业标准结构，集保护层、精度控制层、分流缓冲层及双层加强骨架网于一体，具备卓越的结构刚度。",
      secondaryCta: {
        label: "多层烧结网总览",
        to: "/SinteredMultilayerMeshPage",
      },
      image: {
        src: FIVE_LAYER_IMAGE,
        alt: "五层标准不锈钢烧结丝网复合板",
      },
      cardNote: "工业通用五层标准",
      cardLink: {
        label: "获取技术参数",
        to: "/RequestAQuotePage",
      },
      specs: [
        { label: "过滤精度", value: "1 – 200 µm" },
        { label: "标准材质", value: "AISI 316L / 304L" },
        { label: "标准厚度", value: "1.70 mm 标准公称" },
        { label: "层级构造", value: "5 层扩散固相烧结" },
      ],
    },
    overview: {
      eyebrow: "工业通用基准",
      heading: "经典五层真空扩散焊接架构解析",
      paragraphs: [
        "5 层标准不锈钢烧结丝网是工业多孔金属过滤领域的通用标杆产品。该结构经过数十年的工程验证，将五层不同特性的编织金属丝网在高温高真空烧结炉中压制熔接为一体化刚性板材。",
        "其五层叠合顺序遵循严密的流体与结构工程学规律：第 1 层为保护网，防止外部粗颗粒机械擦伤；第 2 层为微米级核心过滤控制层；第 3 层为流体缓冲与分流均布层；第 4 与第 5 层为双层重型粗目骨架支撑网，赋予整体优异的耐压抗屈服强度。",
        "五层所有网丝交叉节点在固态下产生分子级扩散熔接，杜绝了孔隙滑动与金属丝脱落风险，在连续高压差、反向脉冲吹洗与高温在线化学清洗中表现出高度的尺寸稳定性。",
      ],
      keyPoints: [
        "经典 5 层结构：表面保护层 + 精度控制层 + 分流层 + 双层加固骨架层。",
        "固相扩散焊接锁死所有接触节点，确保微米级孔径长期恒定不变。",
        "能够承受周期性高压反洗与强冲击工况，杜绝滤料介质脱落与纤维脱出。",
      ],
      image: {
        src: FIVE_LAYER_IMAGE,
        alt: "五层标准不锈钢烧结丝网复合板",
      },
    },
    specTable: {
      heading: "5 层标准烧结网常用技术参数表",
      intro: "标准材质提供 AISI 316L 与 304L 不锈钢，公称厚度为 1.70 mm，广泛用于粉体流态化、聚合物熔体及石化精细过滤。",
      columns: [
        { key: "specification", label: "规格型号", align: "left" },
        { key: "nominalRating", label: "公称精度 (µm)", align: "right" },
        { key: "controlMesh", label: "控制层网型", align: "right" },
        { key: "nominalThickness", label: "公称厚度 (mm)", align: "right" },
        { key: "standardDimensions", label: "常用板幅尺寸", align: "left" },
      ],
      rows: [
        {
          specification: "5L-001",
          nominalRating: "1",
          controlMesh: "密纹编织网",
          nominalThickness: "1.70",
          standardDimensions: "500 × 1000 mm / 1000 × 1000 mm",
        },
        {
          specification: "5L-005",
          nominalRating: "5",
          controlMesh: "密纹编织网",
          nominalThickness: "1.70",
          standardDimensions: "500 × 1000 mm / 1000 × 1000 mm",
        },
        {
          specification: "5L-010",
          nominalRating: "10",
          controlMesh: "密纹编织网",
          nominalThickness: "1.70",
          standardDimensions: "500 × 1000 mm / 1000 × 1000 mm",
        },
        {
          specification: "5L-025",
          nominalRating: "25",
          controlMesh: "500 目平纹",
          nominalThickness: "1.70",
          standardDimensions: "500 × 1000 mm / 1000 × 1000 mm",
        },
        {
          specification: "5L-050",
          nominalRating: "50",
          controlMesh: "300 目平纹",
          nominalThickness: "1.70",
          standardDimensions: "500 × 1000 mm / 1000 × 1000 mm",
        },
        {
          specification: "5L-100",
          nominalRating: "100",
          controlMesh: "150 目平纹",
          nominalThickness: "1.70",
          standardDimensions: "500 × 1000 mm / 1000 × 1000 mm",
        },
        {
          specification: "5L-200",
          nominalRating: "200",
          controlMesh: "80 目平纹",
          nominalThickness: "1.70",
          standardDimensions: "500 × 1000 mm / 1000 × 1000 mm",
        },
      ],
      caption: "定制板幅最大可达 1200 × 1500 mm，可按要求进行数控切割与零件深加工。",
    },
    features: {
      heading: "基准工程应用优势",
      intro: "在化工、制药、环保水处理与航空流体系统中得到长期验证。",
      items: [
        {
          title: "极其稳定的孔径精度",
          text: "固相热扩散完全融合交叉焊点，在介质持续高压冲刷下不会出现孔径扩张或局部塌陷。",
        },
        {
          title: "优异的整体机械刚度",
          text: "双层骨架支撑网赋予极高的结构抗弯与抗爆破强度，可直接作为承重自支撑过滤板使用。",
        },
        {
          title: "卓越的反洗与再生性能",
          text: "平整的微孔表面使得沉积滤饼易于反洗剥离，耐受强酸强碱在线 CIP 清洗及超声波处理。",
        },
        {
          title: "全面的机加工适应性",
          text: "便于进行激光精密下料、冲压、滚圆成筒、折波打褶以及高质量氩弧焊接装配。",
        },
      ],
    },
    related: {
      heading: "相关烧结复合网构型",
      intro: "对比 5 层标准板与 3 层大通量网及重型冲孔复合网，选择最匹配工艺的构型。",
      cards: [
        {
          title: "多层烧结丝网总览",
          text: "涵盖 3 层、5 层及多层冲孔复合网在内的全系列高温真空固相扩散焊接产品。",
          to: "/SinteredMultilayerMeshPage",
          image: {
            src: MULTILAYER_HERO_IMAGE,
            alt: "高温真空扩散焊接多层烧结不锈钢丝网复合板",
          },
          badge: "上级系列",
          linkLabel: "查看多层烧结网总览",
        },
        {
          title: "3 层烧结丝网",
          text: "由保护层、微米控制层与支撑层组成，兼顾高通量渗透性能与可靠的机械抗压能力。",
          to: "/ThreeLayerSinteredMeshPage",
          image: {
            src: THREE_LAYER_IMAGE,
            alt: "三层烧结不锈钢丝网微观截面与结构层析",
          },
          badge: "3 层高通量",
          linkLabel: "查看 3 层规格",
        },
        {
          title: "多层定制复合烧结网",
          text: "采用多层丝网与冲孔基板紧密烧结，具备极高抗爆破强度，胜任重载固液分离工况。",
          to: "/MultiLayerPage",
          image: {
            src: MULTI_LAYER_IMAGE,
            alt: "多层不锈钢烧结丝网微观复合结构与扩散焊接截面",
          },
          badge: "重载复合",
          linkLabel: "查看多层规格",
        },
      ],
    },
    cta: {
      id: "layer-standard-sintered-cta",
      eyebrow: "标准现货与定制裁切",
      heading: "获取 5 层标准烧结网报价与样品",
      lead: "互拓常年备有多种精度的 1.70 mm AISI 316L 标板现货，可迅速提供全尺寸整板、精密冲片与成套焊接滤芯部件。",
      secondaryLabel: "联系销售团队",
      secondaryTo: "/ContactPage",
      note: "严格按公称规格供货，按订单要求附带原材料证明与出厂尺寸检验报告。",
    },
  },
  es: {
    breadcrumbKey: "LayerStandardSinteredMesh",
    relatedTopic: "sintered",
    hero: {
      categoryBadge: "Malla multicapa sinterizada",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Malla sinterizada estándar de 5 capas",
      lead: "La configuración industrial universal que combina capa protectora, capa de control, capa de dispersión y dos capas de refuerzo para una rigidez estructural inigualable.",
      secondaryCta: {
        label: "Resumen multicapa",
        to: "/SinteredMultilayerMeshPage",
      },
      image: {
        src: FIVE_LAYER_IMAGE,
        alt: "Estructura de malla sinterizada estándar de 5 capas",
      },
      cardNote: "Estándar industrial universal",
      cardLink: {
        label: "Solicitar especificaciones",
        to: "/RequestAQuotePage",
      },
      specs: [
        { label: "Retención", value: "1 – 200 µm" },
        { label: "Aleaciones estándar", value: "AISI 316L / 304L" },
        { label: "Espesor estándar", value: "1.70 mm nominal" },
        { label: "Construcción", value: "5 capas unidas por difusión" },
      ],
    },
    overview: {
      eyebrow: "Punto de referencia industrial",
      heading: "Arquitectura universal de cinco capas unidas por difusión",
      paragraphs: [
        "La malla sinterizada estándar de 5 capas es el referente adoptado universalmente en la filtración de metales porosos. Integra cinco capas de tela metálica unidas en horno de alto vacío en una placa homogénea.",
        "La disposición sigue una secuencia precisa: la capa 1 actúa como protección contra abrasión; la capa 2 es el control micrométrico; la capa 3 dispersa el flujo de proceso; mientras que las capas 4 y 5 aportan el doble esqueleto de soporte mecánico.",
        "Al estar fundidos los puntos de contacto por difusión en estado sólido, los poros permanecen invariables frente a presiones continuas, golpes de ariete e intensos lavados químicos.",
      ],
      keyPoints: [
        "Estructura universal de 5 niveles: protección, control, dispersión y doble refuerzo.",
        "La difusión en estado sólido mantiene la precisión de corte micrométrica.",
        "Resiste ciclos repetitivos de retrolavado y alta presión diferencial.",
      ],
      image: {
        src: FIVE_LAYER_IMAGE,
        alt: "Estructura de malla sinterizada estándar de 5 capas",
      },
    },
    specTable: {
      heading: "Matriz de especificaciones de 5 capas",
      intro: "Placas estándar producidas en AISI 316L y 304L con un espesor nominal de 1.70 mm.",
      columns: [
        { key: "specification", label: "Modelo", align: "left" },
        { key: "nominalRating", label: "Retención (µm)", align: "right" },
        { key: "controlMesh", label: "Capa de control", align: "right" },
        { key: "nominalThickness", label: "Espesor (mm)", align: "right" },
        { key: "standardDimensions", label: "Dimensiones estándar", align: "left" },
      ],
      rows: [
        {
          specification: "5L-001",
          nominalRating: "1",
          controlMesh: "Tejido reps",
          nominalThickness: "1.70",
          standardDimensions: "500 × 1000 mm / 1000 × 1000 mm",
        },
        {
          specification: "5L-005",
          nominalRating: "5",
          controlMesh: "Tejido reps",
          nominalThickness: "1.70",
          standardDimensions: "500 × 1000 mm / 1000 × 1000 mm",
        },
        {
          specification: "5L-010",
          nominalRating: "10",
          controlMesh: "Tejido reps",
          nominalThickness: "1.70",
          standardDimensions: "500 × 1000 mm / 1000 × 1000 mm",
        },
        {
          specification: "5L-025",
          nominalRating: "25",
          controlMesh: "500 Mesh",
          nominalThickness: "1.70",
          standardDimensions: "500 × 1000 mm / 1000 × 1000 mm",
        },
        {
          specification: "5L-050",
          nominalRating: "50",
          controlMesh: "300 Mesh",
          nominalThickness: "1.70",
          standardDimensions: "500 × 1000 mm / 1000 × 1000 mm",
        },
        {
          specification: "5L-100",
          nominalRating: "100",
          controlMesh: "150 Mesh",
          nominalThickness: "1.70",
          standardDimensions: "500 × 1000 mm / 1000 × 1000 mm",
        },
        {
          specification: "5L-200",
          nominalRating: "200",
          controlMesh: "80 Mesh",
          nominalThickness: "1.70",
          standardDimensions: "500 × 1000 mm / 1000 × 1000 mm",
        },
      ],
      caption: "Medidas especiales hasta 1200 × 1500 mm disponibles bajo pedido de ingeniería.",
    },
    features: {
      heading: "Características técnicas de referencia",
      intro: "Ampliamente contrastada en industria química, farmacéutica y tratamiento de fluidos.",
      items: [
        {
          title: "Estabilidad de poro invariable",
          text: "Los puntos soldados por difusión evitan el ensanchamiento de los poros bajo presiones sostenidas.",
        },
        {
          title: "Rigidez estructural elevada",
          text: "Las dos capas de esqueleto permiten su uso como placas de filtración auto-portantes.",
        },
        {
          title: "Aptitud de limpieza superior",
          text: "La distribución superficial de los poros facilita la rápida separación de la torta de filtración.",
        },
        {
          title: "Capacidad de mecanizado integral",
          text: "Apta para corte por láser, plegado, calandrado de tubos y soldadura TIG a conexiones bridadas.",
        },
      ],
    },
    related: {
      heading: "Construcciones sinterizadas afines",
      intro: "Compare la placa estándar de 5 capas con las soluciones de 3 capas y multicapa reforzada.",
      cards: [
        {
          title: "Malla multicapa sinterizada",
          text: "Familia principal que abarca construcciones estándar y personalizadas unidas por difusión.",
          to: "/SinteredMultilayerMeshPage",
          image: {
            src: MULTILAYER_HERO_IMAGE,
            alt: "Laminado de malla de alambre multicapa sinterizado al vacío",
          },
          badge: "Familia principal",
          linkLabel: "Ver resumen multicapa",
        },
        {
          title: "Malla sinterizada de 3 capas",
          text: "Estructurada con malla exterior protectora, capa de control y soporte para alta permeabilidad.",
          to: "/ThreeLayerSinteredMeshPage",
          image: {
            src: THREE_LAYER_IMAGE,
            alt: "Sección transversal de malla sinterizada de 3 capas",
          },
          badge: "3 capas",
          linkLabel: "Ver detalles de 3 capas",
        },
        {
          title: "Malla compuesta multicapa",
          text: "Mallas multicapa reforzadas diseñadas para soportar picos severos de presión diferencial.",
          to: "/MultiLayerPage",
          image: {
            src: MULTI_LAYER_IMAGE,
            alt: "Vista macro de laminado de malla de acero inoxidable multicapa sinterizado",
          },
          badge: "Refuerzo pesado",
          linkLabel: "Ver detalles multicapa",
        },
      ],
    },
    cta: {
      id: "layer-standard-sintered-cta",
      eyebrow: "Disponibilidad estándar y corte",
      heading: "Solicite cotización de malla sinterizada de 5 capas",
      lead: "Mantenemos stock de placas estándar de 1.70 mm en AISI 316L y 304L para suministro inmediato de planchas y discos cortados.",
      secondaryLabel: "Contactar con ventas",
      secondaryTo: "/ContactPage",
      note: "Suministro con informes de inspección dimensional y certificados de material según pedido.",
    },
  },
  de: {
    breadcrumbKey: "LayerStandardSinteredMesh",
    relatedTopic: "sintered",
    hero: {
      categoryBadge: "Mehrlagen-Sintergewebe",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "5-lagiges Standard-Sintergewebe",
      lead: "Die universelle Industriekonfiguration kombiniert Schutzlage, Filterlage, Dispersionslage und zwei Verstärkungslagen für unübertroffene mechanische Steifigkeit.",
      secondaryCta: {
        label: "Mehrlagen-Übersicht",
        to: "/SinteredMultilayerMeshPage",
      },
      image: {
        src: FIVE_LAYER_IMAGE,
        alt: "Struktur eines 5-lagigen Standard-Sintermetallgewebes",
      },
      cardNote: "Industrieller 5-Lagen-Standard",
      cardLink: {
        label: "Spezifikationen anfragen",
        to: "/RequestAQuotePage",
      },
      specs: [
        { label: "Feinheit", value: "1 – 200 µm" },
        { label: "Standardlegierungen", value: "AISI 316L / 304L" },
        { label: "Nenndicke", value: "1.70 mm Standard" },
        { label: "Aufbau", value: "5 Lagen diffusionsgebunden" },
      ],
    },
    overview: {
      eyebrow: "Industrieller Maßstab",
      heading: "Universelle 5-Lagen-Diffusionsverbundgeometrie",
      paragraphs: [
        "Das 5-lagige Standard-Sinterdrahtgewebe ist der international anerkannte Maßstab in der porösen Metallfiltration. Fünf aufeinander abgestimmte Drahtgewebelagen werden unter Hochvakuum zu einer homogenen porösen Platte diffusionsverschweißt.",
        "Der Schichtaufbau folgt einem bewährten Prinzip: Lage 1 dient als Schutzschicht gegen mechanischen Abrieb; Lage 2 bildet die mikrometergenaue Kontrollschicht; Lage 3 fungiert als Strömungsverteilschicht; die Lagen 4 und 5 bilden ein kräftiges Doppelstützskelett.",
        "Da alle Drahtkreuzungspunkte durch Diffusionsglühen metallurgisch verschmolzen sind, bleibt das Porengerüst auch bei hohen Differenzdrücken, zyklischen Rückspülungen und chemischen CIP-Reinigungen formstabil.",
      ],
      keyPoints: [
        "Klassischer 5-Lagen-Aufbau: Schutzlage + Filterlage + Pufferlage + 2 Stützlagen.",
        "Festkörper-Diffusionsglühen sichert dauerhafte Formstabilität aller Mikron-Poren.",
        "Widersteht zyklischen Rückspülungen und Druckstößen ohne Faserabrieb.",
      ],
      image: {
        src: FIVE_LAYER_IMAGE,
        alt: "Struktur eines 5-lagigen Standard-Sintermetallgewebes",
      },
    },
    specTable: {
      heading: "Standard-Spezifikationen 5-Lagen-Gewebe",
      intro: "Platten in den Werkstoffen AISI 316L und 304L mit Standard-Nenndicke 1.70 mm.",
      columns: [
        { key: "specification", label: "Modell", align: "left" },
        { key: "nominalRating", label: "Feinheit (µm)", align: "right" },
        { key: "controlMesh", label: "Filterlage", align: "right" },
        { key: "nominalThickness", label: "Dicke (mm)", align: "right" },
        { key: "standardDimensions", label: "Standard-Plattenmaße", align: "left" },
      ],
      rows: [
        {
          specification: "5L-001",
          nominalRating: "1",
          controlMesh: "Tressengewebe",
          nominalThickness: "1.70",
          standardDimensions: "500 × 1000 mm / 1000 × 1000 mm",
        },
        {
          specification: "5L-005",
          nominalRating: "5",
          controlMesh: "Tressengewebe",
          nominalThickness: "1.70",
          standardDimensions: "500 × 1000 mm / 1000 × 1000 mm",
        },
        {
          specification: "5L-010",
          nominalRating: "10",
          controlMesh: "Tressengewebe",
          nominalThickness: "1.70",
          standardDimensions: "500 × 1000 mm / 1000 × 1000 mm",
        },
        {
          specification: "5L-025",
          nominalRating: "25",
          controlMesh: "500 Mesh",
          nominalThickness: "1.70",
          standardDimensions: "500 × 1000 mm / 1000 × 1000 mm",
        },
        {
          specification: "5L-050",
          nominalRating: "50",
          controlMesh: "300 Mesh",
          nominalThickness: "1.70",
          standardDimensions: "500 × 1000 mm / 1000 × 1000 mm",
        },
        {
          specification: "5L-100",
          nominalRating: "100",
          controlMesh: "150 Mesh",
          nominalThickness: "1.70",
          standardDimensions: "500 × 1000 mm / 1000 × 1000 mm",
        },
        {
          specification: "5L-200",
          nominalRating: "200",
          controlMesh: "80 Mesh",
          nominalThickness: "1.70",
          standardDimensions: "500 × 1000 mm / 1000 × 1000 mm",
        },
      ],
      caption: "Sondermaße bis 1200 × 1500 mm auf ingenieurtechnische Anfrage.",
    },
    features: {
      heading: "Referenzmerkmale für Industrieanwendungen",
      intro: "Bewährt in Chemie, Pharma, Petrochemie und Fluidisierungsverfahren.",
      items: [
        {
          title: "Konstante Porengeometrie",
          text: "Diffusionsgebundene Drähte schließen Porenerweiterungen unter Betriebsdrücken aus.",
        },
        {
          title: "Hohe mechanische Festigkeit",
          text: "Doppelte Stützlagen verleihen der Platte selbsttragende Biegesteifigkeit.",
        },
        {
          title: "Hervorragende Rückspülung",
          text: "Die glatte Oberfläche begünstigt das schnelle Ablösen von Filterkuchen bei Umkehrspülung.",
        },
        {
          title: "Umfassende Zerspan- und Formbarkeit",
          text: "Laserschneidbar, stanzbar, rollbar zu Zylindern und schweißbar an Sonderflansche.",
        },
      ],
    },
    related: {
      heading: "Verwandte Sintergewebe-Konstruktionen",
      intro: "Vergleichen Sie 5-Lagen-Standardplatten mit 3-lagigen und verstärkten Mehrlagenaufbauten.",
      cards: [
        {
          title: "Mehrlagen-Sinterdrahtgewebe",
          text: "Die übergeordnete Produktfamilie diffusionsgebundener Schichtverbunde.",
          to: "/SinteredMultilayerMeshPage",
          image: {
            src: MULTILAYER_HERO_IMAGE,
            alt: "Vakuum-diffusionsgebundenes Mehrlagen-Sinterdrahtgewebelaminat",
          },
          badge: "Hauptfamilie",
          linkLabel: "Zur Mehrlagen-Übersicht",
        },
        {
          title: "3-lagiges Sintergewebe",
          text: "Aufgebaut aus Schutzlage, Filterlage und offenem Stützgewebe für hohe Durchlässigkeit.",
          to: "/ThreeLayerSinteredMeshPage",
          image: {
            src: THREE_LAYER_IMAGE,
            alt: "Querschnitt eines 3-lagigen Edelstahl-Sintergewebelaminats",
          },
          badge: "3-Lagen",
          linkLabel: "3-Lagen-Details",
        },
        {
          title: "Mehrlagen-Verbundsintergewebe",
          text: "Hochbelastbare Spezialverbunde mit Lochblechverstärkung für abrasive Fest-Flüssig-Trennung.",
          to: "/MultiLayerPage",
          image: {
            src: MULTI_LAYER_IMAGE,
            alt: "Makroansicht eines mehrlagigen gesinterten Edelstahldrahtgewebes",
          },
          badge: "Verstärkter Verbund",
          linkLabel: "Mehrlagen-Details",
        },
      ],
    },
    cta: {
      id: "layer-standard-sintered-cta",
      eyebrow: "Standardlager & Zuschnitte",
      heading: "5-lagiges Standard-Sintergewebe anfragen",
      lead: "Wir führen Standardplatten mit 1.70 mm Dicke in AISI 316L und 304L für kurzfristige Lieferungen von Tafeln, Ronden und Zylindern.",
      secondaryLabel: "Vertrieb kontaktieren",
      secondaryTo: "/ContactPage",
      note: "Standardtafeln und gefertigte Filterelemente mit Prüfzertifikaten gemäß Bestellung.",
    },
  },
  fr: {
    breadcrumbKey: "LayerStandardSinteredMesh",
    relatedTopic: "sintered",
    hero: {
      categoryBadge: "Toile multicouche frittée",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Toile frittée standard 5 couches",
      lead: "La configuration industrielle universelle associant couche protectrice, couche de contrôle, couche de dispersion et deux armatures de renfort pour une rigidité inégalée.",
      secondaryCta: {
        label: "Aperçu multicouche",
        to: "/SinteredMultilayerMeshPage",
      },
      image: {
        src: FIVE_LAYER_IMAGE,
        alt: "Structure de toile métallique frittée 5 couches standard",
      },
      cardNote: "Standard industriel 5 couches",
      cardLink: {
        label: "Demander les spécifications",
        to: "/RequestAQuotePage",
      },
      specs: [
        { label: "Seuil", value: "1 – 200 µm" },
        { label: "Nuances standard", value: "AISI 316L / 304L" },
        { label: "Épaisseur nominale", value: "1.70 mm standard" },
        { label: "Construction", value: "5 couches liées par diffusion" },
      ],
    },
    overview: {
      eyebrow: "Référence industrielle",
      heading: "Architecture universelle à 5 couches unies par diffusion",
      paragraphs: [
        "La toile frittée standard 5 couches représente le standard de référence mondial dans le domaine de la filtration par métaux poreux. Elle superpose cinq couches de toiles métalliques tissées fusionnées sous vide en une plaque uniforme.",
        "L'empilement respecte une logique rigoureuse : la couche 1 protège contre l'abrasion ; la couche 2 garantit le seuil de filtration micrométrique ; la couche 3 assure la diffusion homogène du fluide ; tandis que les couches 4 et 5 constituent une double ossature assurant la tenue mécanique.",
        "La liaison métallurgique par diffusion à l'état solide solidarise chaque croisement de fil, maintenant la régularité des pores face aux fortes pressions, contre-lavages répétés et nettoyages chimiques CIP.",
      ],
      keyPoints: [
        "Structure classique en 5 couches : protection, filtration, dispersion et double renfort.",
        "La diffusion à l'état solide fige tous les croisements pour une précision micrométrique durable.",
        "Résiste aux chocs de pression et contre-lavages répétés sans déformation ni relargage.",
      ],
      image: {
        src: FIVE_LAYER_IMAGE,
        alt: "Structure de toile métallique frittée 5 couches standard",
      },
    },
    specTable: {
      heading: "Matrice des spécifications 5 couches",
      intro: "Plaques standard produites en nuances AISI 316L et 304L d'une épaisseur nominale de 1.70 mm.",
      columns: [
        { key: "specification", label: "Modèle", align: "left" },
        { key: "nominalRating", label: "Seuil (µm)", align: "right" },
        { key: "controlMesh", label: "Toile filtrante", align: "right" },
        { key: "nominalThickness", label: "Épaisseur (mm)", align: "right" },
        { key: "standardDimensions", label: "Formats standard", align: "left" },
      ],
      rows: [
        {
          specification: "5L-001",
          nominalRating: "1",
          controlMesh: "Toile reps",
          nominalThickness: "1.70",
          standardDimensions: "500 × 1000 mm / 1000 × 1000 mm",
        },
        {
          specification: "5L-005",
          nominalRating: "5",
          controlMesh: "Toile reps",
          nominalThickness: "1.70",
          standardDimensions: "500 × 1000 mm / 1000 × 1000 mm",
        },
        {
          specification: "5L-010",
          nominalRating: "10",
          controlMesh: "Toile reps",
          nominalThickness: "1.70",
          standardDimensions: "500 × 1000 mm / 1000 × 1000 mm",
        },
        {
          specification: "5L-025",
          nominalRating: "25",
          controlMesh: "500 Mesh",
          nominalThickness: "1.70",
          standardDimensions: "500 × 1000 mm / 1000 × 1000 mm",
        },
        {
          specification: "5L-050",
          nominalRating: "50",
          controlMesh: "300 Mesh",
          nominalThickness: "1.70",
          standardDimensions: "500 × 1000 mm / 1000 × 1000 mm",
        },
        {
          specification: "5L-100",
          nominalRating: "100",
          controlMesh: "150 Mesh",
          nominalThickness: "1.70",
          standardDimensions: "500 × 1000 mm / 1000 × 1000 mm",
        },
        {
          specification: "5L-200",
          nominalRating: "200",
          controlMesh: "80 Mesh",
          nominalThickness: "1.70",
          standardDimensions: "500 × 1000 mm / 1000 × 1000 mm",
        },
      ],
      caption: "Formats sur mesure jusqu'à 1200 × 1500 mm disponibles sur demande.",
    },
    features: {
      heading: "Avantages de référence",
      intro: "Validée dans les industries chimique, pharmaceutique, pétrochimique et de fluidisation.",
      items: [
        {
          title: "Invariabilité des ouvertures",
          text: "Les fils soudés par diffusion empêchent tout élargissement des mailles sous charge continue.",
        },
        {
          title: "Rigidité structurale élevée",
          text: "La double armature permet d'employer ces plaques comme éléments filtrants autoportants.",
        },
        {
          title: "Facilité de nettoyage",
          text: "La surface lisse favorise le détachement rapide du gâteau de filtration lors des contre-lavages.",
        },
        {
          title: "Aptitude au formage et soudage",
          text: "Se découpe au laser, se plisse et se soude en TIG pour fabriquer cartouches et paniers.",
        },
      ],
    },
    related: {
      heading: "Structures frittées associées",
      intro: "Comparez les plaques 5 couches avec les toiles 3 couches et les composites renforcés.",
      cards: [
        {
          title: "Toile métallique multicouche frittée",
          text: "Gamme principale regroupant nos solutions standards et sur mesure frittées sous vide.",
          to: "/SinteredMultilayerMeshPage",
          image: {
            src: MULTILAYER_HERO_IMAGE,
            alt: "Stratifié de toile métallique multicouche frittée sous vide",
          },
          badge: "Gamme mère",
          linkLabel: "Voir l'aperçu multicouche",
        },
        {
          title: "Toile frittée 3 couches",
          text: "Composée d'une toile protectrice, d'une couche de filtration et d'un support pour grand débit.",
          to: "/ThreeLayerSinteredMeshPage",
          image: {
            src: THREE_LAYER_IMAGE,
            alt: "Coupe transversale d'un stratifié fritté à 3 couches en acier inoxydable",
          },
          badge: "3 couches",
          linkLabel: "Détails 3 couches",
        },
        {
          title: "Toile composite multicouche",
          text: "Composites renforcés avec tôle perforée pour absorber les fortes variations de charge.",
          to: "/MultiLayerPage",
          image: {
            src: MULTI_LAYER_IMAGE,
            alt: "Vue macro d'un stratifié multicouche fritté en acier inoxydable",
          },
          badge: "Renfort lourd",
          linkLabel: "Détails multicouche",
        },
      ],
    },
    cta: {
      id: "layer-standard-sintered-cta",
      eyebrow: "Stock standard & découpe",
      heading: "Consultez notre stock de toiles frittées 5 couches",
      lead: "Nous tenons en stock des plaques 1.70 mm en AISI 316L et 304L pour livraison rapide de panneaux, disques et cylindres usinés.",
      secondaryLabel: "Contacter le service commercial",
      secondaryTo: "/ContactPage",
      note: "Plaques standard et éléments façonnés accompagnés de rapports de contrôle sur demande.",
    },
  },
  pt: {
    breadcrumbKey: "LayerStandardSinteredMesh",
    relatedTopic: "sintered",
    hero: {
      categoryBadge: "Malha multicamadas sinterizada",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Malha sinterizada padrão de 5 camadas",
      lead: "A configuração industrial universal que combina camada protetora, camada de controle, camada de dispersão e dois reforços de sustentação para máxima rigidez.",
      secondaryCta: {
        label: "Visão geral multicamadas",
        to: "/SinteredMultilayerMeshPage",
      },
      image: {
        src: FIVE_LAYER_IMAGE,
        alt: "Estrutura de malha sinterizada padrão de 5 camadas",
      },
      cardNote: "Padrão universal de 5 camadas",
      cardLink: {
        label: "Solicitar dados técnicos",
        to: "/RequestAQuotePage",
      },
      specs: [
        { label: "Retenção", value: "1 – 200 µm" },
        { label: "Ligas padrão", value: "AISI 316L / 304L" },
        { label: "Espessura", value: "1.70 mm padrão" },
        { label: "Construção", value: "5 camadas unidas por difusão" },
      ],
    },
    overview: {
      eyebrow: "Referência industrial",
      heading: "Geometria padrão de cinco camadas unidas por difusão",
      paragraphs: [
        "A malha sinterizada padrão de 5 camadas é a referência universal em processos de filtração com metais porosos. Reúne cinco malhas de aço inoxidável unidas por difusão atômica sob alto vácuo em uma placa rígida.",
        "A montagem respeita uma ordem técnica precisa: camada 1 para proteção externa contra atrito; camada 2 com malha de retenção de alta precisão; camada 3 para dispersão de fluxo; e camadas 4 e 5 constituindo uma armadura dupla de alta resistência mecânica.",
        "A união sólida nos pontos de contato mantém a abertura dos poros inalterada durante pulsos severos de retrolavagem, variações de pressão e ciclos químicos de limpeza CIP.",
      ],
      keyPoints: [
        "Estrutura universal de 5 estágios: proteção, retenção, dispersão e duplo reforço.",
        "A sinterização a vácuo bloqueia deslocamentos de fios para garantir retenção constante.",
        "Suporta retrolavagens contínuas e alta pressão diferencial sem perda de integridade.",
      ],
      image: {
        src: FIVE_LAYER_IMAGE,
        alt: "Estrutura de malha sinterizada padrão de 5 camadas",
      },
    },
    specTable: {
      heading: "Tabela de especificações de 5 camadas",
      intro: "Chapas padrão fabricadas em AISI 316L e 304L com espessura nominal de 1.70 mm.",
      columns: [
        { key: "specification", label: "Modelo", align: "left" },
        { key: "nominalRating", label: "Retenção (µm)", align: "right" },
        { key: "controlMesh", label: "Malha de retenção", align: "right" },
        { key: "nominalThickness", label: "Espessura (mm)", align: "right" },
        { key: "standardDimensions", label: "Dimensões padrão", align: "left" },
      ],
      rows: [
        {
          specification: "5L-001",
          nominalRating: "1",
          controlMesh: "Trama holandesa",
          nominalThickness: "1.70",
          standardDimensions: "500 × 1000 mm / 1000 × 1000 mm",
        },
        {
          specification: "5L-005",
          nominalRating: "5",
          controlMesh: "Trama holandesa",
          nominalThickness: "1.70",
          standardDimensions: "500 × 1000 mm / 1000 × 1000 mm",
        },
        {
          specification: "5L-010",
          nominalRating: "10",
          controlMesh: "Trama holandesa",
          nominalThickness: "1.70",
          standardDimensions: "500 × 1000 mm / 1000 × 1000 mm",
        },
        {
          specification: "5L-025",
          nominalRating: "25",
          controlMesh: "500 Mesh",
          nominalThickness: "1.70",
          standardDimensions: "500 × 1000 mm / 1000 × 1000 mm",
        },
        {
          specification: "5L-050",
          nominalRating: "50",
          controlMesh: "300 Mesh",
          nominalThickness: "1.70",
          standardDimensions: "500 × 1000 mm / 1000 × 1000 mm",
        },
        {
          specification: "5L-100",
          nominalRating: "100",
          controlMesh: "150 Mesh",
          nominalThickness: "1.70",
          standardDimensions: "500 × 1000 mm / 1000 × 1000 mm",
        },
        {
          specification: "5L-200",
          nominalRating: "200",
          controlMesh: "80 Mesh",
          nominalThickness: "1.70",
          standardDimensions: "500 × 1000 mm / 1000 × 1000 mm",
        },
      ],
      caption: "Formatos sob medida até 1200 × 1500 mm sob encomenda de engenharia.",
    },
    features: {
      heading: "Diferenciais de referência",
      intro: "Comprovada em indústrias químicas, farmacêuticas, petroquímicas e processos de fluidização.",
      items: [
        {
          title: "Estabilidade geométrica de poros",
          text: "Os cruzamentos fundidos por difusão impedem a alteração de abertura sob fluxo pressurizado contínuo.",
        },
        {
          title: "Alta resistência mecânica",
          text: "As camadas duplas de reforço permitem que a placa funcione de forma autoportante.",
        },
        {
          title: "Excelente facilidade de limpeza",
          text: "Superfície uniforme favorece o desprendimento rápido de sólidos durante a retrolavagem.",
        },
        {
          title: "Completa usinabilidade",
          text: "Pode ser cortada a laser, estampada em discos, calandrada e soldada em flanges.",
        },
      ],
    },
    related: {
      heading: "Telas sinterizadas relacionadas",
      intro: "Compare a placa padrão de 5 camadas com os modelos de 3 camadas e multicamadas reforçadas.",
      cards: [
        {
          title: "Malha multicamadas sinterizada",
          text: "Família principal que engloba estruturas sinterizadas de 3 e 5 camadas e modelos especiais.",
          to: "/SinteredMultilayerMeshPage",
          image: {
            src: MULTILAYER_HERO_IMAGE,
            alt: "Laminado de malha de arame multicamadas sinterizado a vácuo",
          },
          badge: "Linha principal",
          linkLabel: "Ver visão geral multicamadas",
        },
        {
          title: "Malha sinterizada de 3 camadas",
          text: "Desenvolvida com malha de proteção, controle e suporte para alta permeabilidade e vazão.",
          to: "/ThreeLayerSinteredMeshPage",
          image: {
            src: THREE_LAYER_IMAGE,
            alt: "Corte transversal de malha sinterizada de 3 camadas em aço inoxidável",
          },
          badge: "3 camadas",
          linkLabel: "Ver detalhes de 3 camadas",
        },
        {
          title: "Malha composta multicamadas",
          text: "Telas multicamadas reforçadas para suportar picos severos de pressão diferencial e meios abrasivos.",
          to: "/MultiLayerPage",
          image: {
            src: MULTI_LAYER_IMAGE,
            alt: "Visão macro de laminado multicamadas sinterizado de aço inoxidável",
          },
          badge: "Refuerzo pesado",
          linkLabel: "Ver detalhes multicamadas",
        },
      ],
    },
    cta: {
      id: "layer-standard-sintered-cta",
      eyebrow: "Estoque e corte sob medida",
      heading: "Solicite cotação da malha sinterizada padrão de 5 camadas",
      lead: "Mantemos estoque de chapas de 1.70 mm em AISI 316L e 304L para pronto fornecimento de painéis, discos e cartuchos montados.",
      secondaryLabel: "Falar com vendas",
      secondaryTo: "/ContactPage",
      note: "Chapas padrão e peças fabricadas fornecidas com relatórios de inspeção sob encomenda.",
    },
  },
};
